import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router";
import { Button } from "antd";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import AddDiscrepancyModal from "./AddDiscrepancyModal";
import { useNotifications } from "@/hooks/useNotifications";
import { useFetch, is403ApiError } from "@/hooks/FetchHook";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import useTableState from "@/hooks/useTableState";
import { ColumnVisibility, ColumnsType } from "@/types/tableState.types";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import { createColumns } from "./discrepanciesTableConfig";
import { constructQueryString } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import Loader from "@/components/UI/Loader";
import { getColumnTitle, useTableHeight } from "@/utils/tableHelpers";
import PageHeader from "@/components/UI/PageHeader";
import { BASE_ENDPOINT } from "@/components/Templates/constants";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useDiscrepanciesFilters } from "@/hooks/useDiscrepanciesFilters";
import { DiscrepanciesDataTable } from "@/components/DataDiscrepancies/DiscrepanciesDataTable";
import type { AssigneeOption } from "./Components";
import type {
  Discrepancy,
  DiscrepancyData,
  DiscrepanciesMetadata,
  AllDiscrepanciesProps,
  AllDiscrepanciesComponentProps,
} from "./dataDiscrepancies.types";

const DATASET_TYPE = "issues_management";

const hasValidAssignee = (assignee: { name?: string; id?: string }) => {
  return Boolean(
    assignee?.id && assignee?.name && assignee.name.trim().length > 0,
  );
};

const AllDiscrepancies = ({
  singleSubjectId,
  openIssueId,
}: AllDiscrepanciesProps) => {
  const pageName = DATASET_TYPE;

  const queryString = constructQueryString({
    baseParams: { dataset_type: pageName },
    filters: { subject: [singleSubjectId ?? null] },
  });

  // Fetch metadata and workspace in parallel
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata,
  } = useFetch({
    endpointSuffix: `filters/${queryString}`,
  });

  const {
    currentWorkspace,
    setCurrentWorkspace,
    workspaces,
    isLoading: workspaceLoading,
    error: workspaceError,
    initialTableState,
    mutations: workspaceMutations,
  } = useCurrentWorkspace(
    pageName,
    metadata as any,
    "all",
    Boolean(singleSubjectId),
  );

  const isLoading = metadataLoading || workspaceLoading;
  const error = metadataError || workspaceError;

  if (error) {
    return (
      <div className="flex mt-25 justify-center items-center">
        {is403ApiError(error) ? <Error403 /> : <ErrorComponent />}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <AllDiscrepanciesComponent
      metadata={metadata as DiscrepanciesMetadata}
      refetchMetadata={refetchMetadata}
      singleSubjectId={singleSubjectId}
      openIssueId={openIssueId}
      currentWorkspace={currentWorkspace}
      setCurrentWorkspace={setCurrentWorkspace}
      workspaces={workspaces}
      initialTableState={initialTableState}
      workspaceMutations={workspaceMutations}
    />
  );
};

const AllDiscrepanciesComponent = ({
  metadata,
  refetchMetadata,
  singleSubjectId,
  openIssueId,
  currentWorkspace,
  setCurrentWorkspace,
  workspaces,
  initialTableState,
  workspaceMutations,
}: AllDiscrepanciesComponentProps) => {
  const { id, buildUrl } = useScopeParams();
  const location = useLocation();

  const wikiLink = singleSubjectId
    ? "/wiki/guides/exploration-singlesubject-data-discrepancies"
    : "/wiki/guides/data-discrepancies";

  const [selectedRecord, setSelectedRecord] = useState<Discrepancy | null>(
    null,
  );
  const [addDiscrepancyOpen, setAddDiscrepancyOpen] = useState(false);
  const canAddDiscrepancy = useFeatureFlag(FEATURE_FLAGS.ADD_DISCREPANCY);
  // Caps toolbar dropdown menus to the space below the page top; the table
  // itself measures its own height inside PaginatedTable.
  const pageRef = useRef<HTMLDivElement>(null);
  const { tableHeight: maxMenuHeight } = useTableHeight({ tableRef: pageRef });

  const pageName = DATASET_TYPE;
  const fullPageName = singleSubjectId ? `${pageName}-ssj` : pageName;
  const [, setSearchParams] = useSearchParams();

  const columnsTitleCreator = useCallback(
    (column: string) => {
      const colMeta = metadata?.columns.find((c: any) => c.field === column);
      return (colMeta as any)?.label || getColumnTitle(column);
    },
    [metadata?.columns],
  );

  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: {
      ...initialTableState,
      pagination: {
        ...initialTableState.pagination,
        total: metadata.row_count,
      },
    },
    pageName: singleSubjectId ? `${pageName}-${singleSubjectId}` : pageName,
    isSingleSubject: Boolean(singleSubjectId),
    trialId: id,
    metadataColumns: metadata.columns.map((c: any) => c.field),
    metadataFilterKeys: Object.keys(metadata.filters || {}),
  });

  const { refetch: refetchNotifications } = useNotifications(id, {
    mode: "unread",
    enabled: false,
  });

  const refetchOnStatusChange = useCallback(() => {
    refetchNotifications();
    refetchMetadata();
  }, [refetchNotifications, refetchMetadata]);

  const handleRowClick = useCallback((record: Discrepancy) => {
    setSelectedRecord(record);
  }, []);

  const availableAssignees = useMemo(() => {
    const assigneesList = metadata?.lookup_fields?.assignees || [];
    return assigneesList.filter(hasValidAssignee).map(
      (assignee: { name: string; id: string }): AssigneeOption => ({
        label: assignee.name,
        value: assignee.id,
      }),
    );
  }, [metadata?.lookup_fields?.assignees]);

  const assigneeIdToName = useMemo(() => {
    const list = metadata?.lookup_fields?.assignees || [];
    const map: Record<string, string> = {};
    list.forEach((assignee: { name: string; id: string }) => {
      if (assignee.name.trim().length > 0) map[assignee.id] = assignee.name;
    });
    return map;
  }, [metadata?.lookup_fields?.assignees]);

  const {
    filterOptionsWithoutDefault,
    selectedFiltersWithoutDefault,
    payload,
  } = useDiscrepanciesFilters({
    metadata,
    filters: currentTableState.filters,
    singleSubjectId,
    pagination: currentTableState.pagination as any,
    sorting: currentTableState.sorting as any,
  });

  const {
    data: discrepancyDataRaw,
    isLoading: loadingDiscrepancyData,
    error: discrepancyDataError,
    queryKey,
    refetch,
  } = useFetch({
    endpointSuffix: `${BASE_ENDPOINT}/`,
    method: "POST",
    body: { dataset_type: DATASET_TYPE, ...payload },
    callback: (data) => {
      if (currentTableState.pagination.total !== data.count) {
        tableStateFunctions.setPagination({
          ...currentTableState.pagination,
          total: data.count,
        });
      }
    },
  });
  const discrepancyData = discrepancyDataRaw as DiscrepancyData | undefined;

  // Refetch on route change to pick up edits made in the single-subject view
  useEffect(() => {
    refetchMetadata();
    refetch();
  }, [location.pathname, refetch, refetchMetadata]);

  // Open drawer for a specific issue when navigated here with ?openIssue=
  useEffect(() => {
    if (openIssueId && discrepancyData?.results) {
      const discrepancyToOpen = discrepancyData.results.find(
        (d) => d.issue_id === openIssueId || d.discrepancy_id === openIssueId,
      );
      if (discrepancyToOpen) {
        setSelectedRecord(discrepancyToOpen);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("openIssue");
            return next;
          },
          { replace: true },
        );
      }
    }
  }, [discrepancyData?.results, openIssueId, setSearchParams]);

  const columns = useMemo(
    () =>
      createColumns(
        currentTableState.columns
          .filter(
            (column: ColumnsType) =>
              column.visibility === ColumnVisibility.VISIBLE,
          )
          .map((column: ColumnsType) => column.fieldName),
        handleRowClick,
        currentTableState.lockedColumns,
        availableAssignees,
        queryKey,
        buildUrl,
        assigneeIdToName,
        refetchOnStatusChange,
        columnsTitleCreator,
      ),
    [
      currentTableState.columns,
      currentTableState.lockedColumns,
      handleRowClick,
      availableAssignees,
      queryKey,
      buildUrl,
      assigneeIdToName,
      refetchOnStatusChange,
      columnsTitleCreator,
    ],
  );

  return (
    <div ref={pageRef} className="w-full">
      <TableToolbar
        // TableToolbar expects a viewSwitcher slot here, but this page needs
        // the header layout instead of a table/plots toggle.
        viewSwitcher={
          <div className="flex items-center gap-3">
            <PageHeader
              title="Discrepancies"
              tooltipText="Explore, assign, and organize data discrepancies that have been automatically identified by Vivo."
            />
            {canAddDiscrepancy && (
              <Button
                type="primary"
                onClick={() => setAddDiscrepancyOpen(true)}
              >
                + Add discrepancy
              </Button>
            )}
          </div>
        }
        workspace={{
          tableName: fullPageName,
          workspaces,
          currentWorkspace,
          setCurrentWorkspace,
          mutations: workspaceMutations,
          initialTableState,
        }}
        currentTableState={{
          ...currentTableState,
          filters: selectedFiltersWithoutDefault,
        }}
        tableStateFunctions={tableStateFunctions}
        tableMetadata={
          { ...metadata, filters: filterOptionsWithoutDefault } as any
        }
        maxMenuHeight={maxMenuHeight}
        fullPageName={fullPageName}
        endpointSuffix={BASE_ENDPOINT}
        name={pageName}
        datasetName={DATASET_TYPE}
        guideLink={wikiLink}
      />
      <div className="mb-4">
        <AppliedFiltersSection
          filters={selectedFiltersWithoutDefault}
          clearFilters={() => tableStateFunctions.setFilters({})} //TODO: create a clearFilters function inside useAdvancedFilters
          setFilters={tableStateFunctions.setFilters}
          filterOptions={filterOptionsWithoutDefault}
          allFiltersData={metadata?.filters}
          columnsTitleCreator={columnsTitleCreator}
        />
      </div>
      <DiscrepanciesDataTable
        data={discrepancyData?.results}
        columns={columns as any}
        loading={loadingDiscrepancyData}
        error={discrepancyDataError}
        pagination={currentTableState.pagination}
        setPagination={tableStateFunctions.setPagination}
        rowCount={metadata.row_count}
        selectedCount={discrepancyData?.count}
        selectedRecord={selectedRecord}
        setSelectedRecord={setSelectedRecord}
        queryKey={queryKey}
        availableAssignees={availableAssignees}
        assigneeIdToName={assigneeIdToName}
        onAssignmentChange={refetchOnStatusChange}
      />
      {canAddDiscrepancy && (
        <AddDiscrepancyModal
          open={addDiscrepancyOpen}
          onClose={() => setAddDiscrepancyOpen(false)}
        />
      )}
    </div>
  );
};

export default AllDiscrepancies;
